/**
 * @module ViewControls
 */
import { Box, Button, createStyles, Grid, IconButton, makeStyles, Typography } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { FieldVisibility } from '@sensenet/default-content-types';
import { useRepository } from '@sensenet/hooks-react';
import React, { createElement, useEffect, useState } from 'react';
import { reactControlMapper } from "../react-control-mapper.js";
import { DEFAULT_GROUP_KEY } from "./edit-view.js";
/**
 * Interface for BrowseView properties
 */
const useStyles = makeStyles(theme => {
  return createStyles({
    grid: {
      margin: '0 auto'
    },
    actionButtonWrapper: {
      textAlign: 'right'
    },
    field: {
      width: '780px',
      margin: 'auto',
      maxWidth: '100%'
    },
    cancel: {},
    advancedFieldContainer: {
      padding: '15px',
      fontSize: '18px',
      width: '780px',
      maxWidth: '100%',
      margin: 'auto'
    },
    advancedFieldBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '0 auto'
    },
    divider: {
      height: '1px',
      margin: '16px auto',
      backgroundColor: theme.palette.primary.main
    }
  });
});
/**
 * View Control for browsing a Content, works with a single Content and based on the ReactControlMapper
 */
export const BrowseView = props => {
  var _props$localization2, _props$localization3;
  const controlMapper = props.controlMapper || reactControlMapper(props.repository);
  const [schema, setSchema] = useState(controlMapper.getFullSchemaForContentType(props.content.Type, 'browse'));
  const classes = useStyles(props);
  const [advancedFields, setAdvancedFields] = useState([]);
  const [advancedFieldStateGroup, setAdvancedFieldStateGroup] = useState([]);
  const repository = useRepository();
  useEffect(() => {
    const schemaObservable = repository.schemas.subscribeToSchemas(() => {
      setSchema(() => controlMapper.getFullSchemaForContentType(props.content.Type, 'browse'));
    });
    return () => schemaObservable.dispose();
  }, [repository.schemas, props.content.Type, controlMapper]);
  useEffect(() => {
    if (schema) {
      const groups = [{
        key: DEFAULT_GROUP_KEY,
        fields: []
      }];
      schema.fieldMappings.forEach(e => {
        if (e.fieldSettings.VisibleBrowse === FieldVisibility.Advanced) {
          var _e$fieldSettings$Cust, _e$fieldSettings$Cust2;
          const category = (_e$fieldSettings$Cust = e.fieldSettings.Customization) === null || _e$fieldSettings$Cust === void 0 ? void 0 : (_e$fieldSettings$Cust2 = _e$fieldSettings$Cust.Categories) === null || _e$fieldSettings$Cust2 === void 0 ? void 0 : _e$fieldSettings$Cust2.split(' ')[0];
          if (category) {
            const group = groups.find(g => g.key === category);
            if (group) {
              group.fields.push(e);
            } else {
              groups.push({
                key: category,
                fields: [e]
              });
            }
          } else {
            var _groups$find;
            (_groups$find = groups.find(g => g.key === DEFAULT_GROUP_KEY)) === null || _groups$find === void 0 ? void 0 : _groups$find.fields.push(e);
          }
        }
      });
      setAdvancedFieldStateGroup(groups.map(g => {
        return {
          key: g.key,
          isOpened: false
        };
      }));
      setAdvancedFields(groups);
    }
  }, [schema]);
  const renderField = field => {
    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
      xl: 12,
      key: field.fieldSettings.Name
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.field
    }, /*#__PURE__*/createElement(controlMapper.getControlForContentField(props.content.Type, field.fieldSettings.Name, 'browse'), {
      actionName: 'browse',
      settings: field.fieldSettings,
      content: props.content,
      fieldValue: props.content[field.fieldSettings.Name],
      renderIcon: props.renderIcon,
      repository: props.repository,
      localization: props.fieldLocalization,
      locale: props.locale
    })));
  };
  const toggleAdvancedFieldGroup = key => {
    setAdvancedFieldStateGroup(prevItems => prevItems.map(item => item.key === key ? {
      ...item,
      isOpened: !item.isOpened
    } : item));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, props.renderTitle ? props.renderTitle() : /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    gutterBottom: true
  }, schema.schema.DisplayName), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    className: classes.grid
  }, schema.fieldMappings.filter(i => !advancedFields.flatMap(g => g.fields).map(g => g.fieldSettings.Name).includes(i.fieldSettings.Name)).sort((item1, item2) => (item2.fieldSettings.FieldIndex || 0) - (item1.fieldSettings.FieldIndex || 0)).map(field => renderField(field)), /*#__PURE__*/React.createElement(Box, {
    className: classes.advancedFieldContainer,
    "data-test": "advanced-field-container"
  }, advancedFields.map((group, index) => {
    var _props$localization$a, _props$localization, _advancedFieldStateGr, _advancedFieldStateGr2;
    return group.fields.length > 0 ? /*#__PURE__*/React.createElement(Box, {
      key: index,
      "data-test": "group-container"
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.divider
    }), /*#__PURE__*/React.createElement(Box, {
      "data-test": "group-header"
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.advancedFieldBox
    }, /*#__PURE__*/React.createElement("span", {
      "data-test": "advanced-field-group-title"
    }, `${(_props$localization$a = (_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.advancedFields) !== null && _props$localization$a !== void 0 ? _props$localization$a : 'Advanced fields'}${group.key === DEFAULT_GROUP_KEY ? '' : ` - ${group.key}`}`), /*#__PURE__*/React.createElement(IconButton, {
      onClick: () => toggleAdvancedFieldGroup(group.key)
    }, (_advancedFieldStateGr = advancedFieldStateGroup.find(g => g.key === group.key)) !== null && _advancedFieldStateGr !== void 0 && _advancedFieldStateGr.isOpened ? /*#__PURE__*/React.createElement(KeyboardArrowUp, null) : /*#__PURE__*/React.createElement(KeyboardArrowDown, null)))), ((_advancedFieldStateGr2 = advancedFieldStateGroup.find(g => g.key === group.key)) === null || _advancedFieldStateGr2 === void 0 ? void 0 : _advancedFieldStateGr2.isOpened) && group.fields.sort((item1, item2) => (item2.fieldSettings.FieldIndex || 0) - (item1.fieldSettings.FieldIndex || 0)).map(field => renderField(field))) : /*#__PURE__*/React.createElement(React.Fragment, null);
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.actionButtonWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-label": ((_props$localization2 = props.localization) === null || _props$localization2 === void 0 ? void 0 : _props$localization2.close) || 'Close',
    color: "default",
    className: classes.cancel,
    onClick: () => {
      var _props$handleCancel;
      return (_props$handleCancel = props.handleCancel) === null || _props$handleCancel === void 0 ? void 0 : _props$handleCancel.call(props);
    }
  }, ((_props$localization3 = props.localization) === null || _props$localization3 === void 0 ? void 0 : _props$localization3.close) || 'Close')));
};
//# sourceMappingURL=browse-view.js.map